import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Trash2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'
import { CurrencyPriceInput } from '@/components/form/currency-price-input'
import { SearchProductVariantInput } from '@/components/form/search-product-variant-input'
import { SearchProductPriceInput } from '@/components/form/search-product-price-input'
import { SearchProductSingleVariantInput } from '@/components/form/search-product-single-variant-input'
import { Action } from './promotion-form'
import { useRowFieldErrors } from './use-row-field-errors'

type ActionType =
    | 'LineItemAdjustment'
    | 'OrderAdjustment'
    | 'ShippingAdjustment'
    | 'CreateLineItem'
    | 'SubscriptionLineItemFreeTrial'
    | 'BOGO'

interface ActionCardProps {
    action: Action
    index: number
    storeId: string
    onUpdate: (index: number, updates: Partial<Action>) => void
    onRemove: (index: number) => void
    /** Hide BXGY-style line-item targeting (target product, max quantity,
     *  applies-on-each-item). Irrelevant for VIP discounts, which apply to the
     *  whole member order. */
    hideAdvancedTargeting?: boolean
}

export const ActionCard = ({ action, index, storeId, onUpdate, onRemove, hideAdvancedTargeting }: ActionCardProps) => {
    const { t } = useTranslation('stores')
    const { hasFieldError, renderFieldError } = useRowFieldErrors('actions', index)

    const getActionLabel = () => {
        switch (action.type) {
            case 'LineItemAdjustment':
                return t('promotion.promotionActions.types.productDiscount')
            case 'OrderAdjustment':
                return t('promotion.promotionActions.types.orderDiscount')
            case 'ShippingAdjustment':
                return t('promotion.promotionActions.types.freeShipping')
            case 'CreateLineItem':
                return t('promotion.promotionActions.types.addFreeProduct')
            case 'SubscriptionLineItemFreeTrial':
                return t('promotion.promotionActions.types.freeTrial')
            case 'BOGO':
                return t('promotion.promotionActions.types.bogo')
            default:
                return action.type
        }
    }

    return (
        <div className="rounded-lg border p-4 space-y-3">
            <div className="flex items-center justify-between border-b pb-3">
                <h4 className="text-sm font-semibold">{getActionLabel()}</h4>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => onRemove(index)}
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                    <Trash2 className="h-4 w-4" />
                </Button>
            </div>

            {(action.type === 'LineItemAdjustment' || action.type === 'OrderAdjustment') && (
                <div className="space-y-3">
                    {action.type === 'LineItemAdjustment' && !hideAdvancedTargeting && (
                        <div>
                            <Label className="text-xs">{t('promotion.promotionActions.targetProduct')}</Label>
                            <div className="mt-1">
                                <SearchProductVariantInput
                                    storeId={storeId}
                                    includeInactive
                                    productId={action.targetProductId}
                                    selectedVariantIds={action.targetVariantIds || []}
                                    onChange={({ productId, variantIds }) =>
                                        onUpdate(index, { targetProductId: productId, targetVariantIds: variantIds })
                                    }
                                    onClear={() =>
                                        onUpdate(index, { targetProductId: undefined, targetVariantIds: undefined })
                                    }
                                />
                            </div>
                        </div>
                    )}

                    <div>
                        <Label className="text-xs">{t('promotion.promotionActions.discountType')}</Label>
                        <div className="bg-muted/40 mt-1 inline-flex items-center rounded-md border p-0.5">
                            {(['percentage', 'amount'] as const).map((type) => {
                                const selected = action.adjustmentType === type
                                return (
                                    <button
                                        key={type}
                                        type="button"
                                        onClick={() => onUpdate(index, { adjustmentType: type })}
                                        className={cn(
                                            'rounded-sm px-3 py-1 text-xs font-medium transition-colors',
                                            selected
                                                ? 'bg-primary text-primary-foreground shadow-sm'
                                                : 'text-muted-foreground hover:text-foreground',
                                        )}
                                    >
                                        {type === 'percentage'
                                            ? t('promotion.promotionActions.percentage')
                                            : t('promotion.promotionActions.fixedAmount')}
                                    </button>
                                )
                            })}
                        </div>
                    </div>

                    {action.adjustmentType === 'percentage' && (
                        <div>
                            <Label className="text-xs">{t('promotion.promotionActions.discountPercentage')}</Label>
                            <Input
                                type="number"
                                step="0.01"
                                value={action.adjustmentPercentage || 0}
                                onChange={(e) => onUpdate(index, { adjustmentPercentage: parseFloat(e.target.value) || 0 })}
                                className="mt-1 h-8"
                                min={0}
                                max={100}
                                error={hasFieldError('adjustmentPercentage')}
                            />
                            {renderFieldError('adjustmentPercentage')}
                        </div>
                    )}
                    {action.adjustmentType === 'amount' && (
                        <div>
                            <Label className="text-xs">{t('promotion.promotionActions.discountAmount')}</Label>
                            <div className="mt-1">
                                <CurrencyPriceInput
                                    storeId={storeId}
                                    values={action.adjustmentAmount || {}}
                                    onChange={({ currencyOptions }) => onUpdate(index, { adjustmentAmount: currencyOptions })}
                                />
                            </div>
                            {renderFieldError('adjustmentAmount')}
                        </div>
                    )}

                    {action.type === 'LineItemAdjustment' && !hideAdvancedTargeting && (
                        <>
                            <div>
                                <Label className="text-xs">{t('promotion.promotionActions.maxQuantityDiscounted')}</Label>
                                <Input
                                    type="number"
                                    value={action.maxQuantityDiscounted || ''}
                                    onChange={(e) => onUpdate(index, { maxQuantityDiscounted: parseInt(e.target.value) || undefined })}
                                    placeholder={t('promotion.promotionActions.unlimitedQuantity')}
                                    className="mt-1 h-8"
                                    min={1}
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                    {t('promotion.promotionActions.maxQuantityHelperText')}
                                </p>
                            </div>

                            <div className="flex items-center space-x-2">
                                <Checkbox
                                    id={`applies-on-each-${index}`}
                                    checked={action.appliesOnEachItem || false}
                                    onCheckedChange={(checked) => onUpdate(index, { appliesOnEachItem: checked === true })}
                                />
                                <Label htmlFor={`applies-on-each-${index}`} className="text-xs font-normal cursor-pointer">
                                    {t('promotion.promotionActions.appliesOnEachItem')}
                                </Label>
                            </div>
                        </>
                    )}
                </div>
            )}

            {action.type === 'CreateLineItem' && (
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs">{t('promotion.promotionActions.productToAdd')}</Label>
                        <div className="mt-1">
                            <SearchProductPriceInput
                                storeId={storeId}
                                includeInactive
                                priceId={action.priceIdToAdd}
                                onChange={(value) =>
                                    onUpdate(index, {
                                        productIdToAdd: value.productId,
                                        variantIdToAdd: value.variantId,
                                        priceIdToAdd: value.priceId,
                                    })
                                }
                                onClear={() =>
                                    onUpdate(index, {
                                        productIdToAdd: undefined,
                                        variantIdToAdd: undefined,
                                        priceIdToAdd: undefined,
                                    })
                                }
                            />
                        </div>
                        {renderFieldError('priceIdToAdd')}
                    </div>
                    <div>
                        <Label className="text-xs">{t('promotion.promotionActions.quantityToAdd')}</Label>
                        <Input
                            type="number"
                            min={1}
                            max={100}
                            value={action.quantityToAdd || 1}
                            onChange={(e) => onUpdate(index, { quantityToAdd: parseInt(e.target.value) || 1 })}
                            className="h-9 max-w-[120px] text-sm"
                        />
                    </div>
                </div>
            )}

            {action.type === 'SubscriptionLineItemFreeTrial' && (
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs">{t('promotion.promotionActions.trialDuration')}</Label>
                        <Input
                            type="number"
                            value={action.subscriptionFreeTrialDuration || 7}
                            onChange={(e) =>
                                onUpdate(index, { subscriptionFreeTrialDuration: parseInt(e.target.value) || 7 })
                            }
                            className="mt-1 h-8"
                            min={1}
                            error={hasFieldError('subscriptionFreeTrialDuration')}
                        />
                        {renderFieldError('subscriptionFreeTrialDuration')}
                    </div>
                    <div>
                        <Label className="text-xs">{t('promotion.promotionActions.durationType')}</Label>
                        <Select
                            value={action.subscriptionFreeTrialDurationType || 'day'}
                            onValueChange={(value) =>
                                onUpdate(index, {
                                    subscriptionFreeTrialDurationType: value as 'day' | 'week' | 'month' | 'year',
                                })
                            }
                        >
                            <SelectTrigger className="mt-1 h-8">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="day">{t('promotion.promotionActions.durationTypes.day')}</SelectItem>
                                <SelectItem value="week">{t('promotion.promotionActions.durationTypes.week')}</SelectItem>
                                <SelectItem value="month">{t('promotion.promotionActions.durationTypes.month')}</SelectItem>
                                <SelectItem value="year">{t('promotion.promotionActions.durationTypes.year')}</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            )}

            {action.type === 'ShippingAdjustment' && (
                <div className="space-y-3">
                    <p className="text-muted-foreground text-sm">{t('promotion.promotionActions.freeShippingDescription')}</p>
                    <div>
                        <Label className="text-xs">{t('promotion.promotionActions.maxShippingAmount')}</Label>
                        <div className="mt-1">
                            <CurrencyPriceInput
                                storeId={storeId}
                                values={action.maxShippingAmount || {}}
                                onChange={({ currencyOptions }) => onUpdate(index, { maxShippingAmount: currencyOptions })}
                            />
                        </div>
                        <p className="mt-1 text-xs text-gray-500">
                            {t('promotion.promotionActions.maxShippingAmountHelperText')}
                        </p>
                    </div>
                </div>
            )}

            {action.type === 'BOGO' && (
                <div className="space-y-3">
                    <div className="flex items-start gap-3">
                        <div className="w-[120px] shrink-0">
                            <Label className="text-xs">{t('promotion.promotionActions.buyQuantityThreshold')}</Label>
                            <Input
                                type="number"
                                value={action.buyQuantityThreshold || 1}
                                onChange={(e) => onUpdate(index, { buyQuantityThreshold: parseInt(e.target.value) || 1 })}
                                className="mt-1 h-8"
                                min={1}
                            />
                        </div>
                        <div className="min-w-0 flex-1">
                            <Label className="text-xs">{t('promotion.promotionActions.buyProduct')}</Label>
                            <div className="mt-1">
                                <SearchProductVariantInput
                                    storeId={storeId}
                                    includeInactive
                                    productId={action.targetProductId}
                                    selectedVariantIds={action.targetVariantIds || []}
                                    onChange={({ productId, variantIds }) =>
                                        onUpdate(index, { targetProductId: productId, targetVariantIds: variantIds })
                                    }
                                    onClear={() =>
                                        onUpdate(index, { targetProductId: undefined, targetVariantIds: undefined })
                                    }
                                />
                            </div>
                            {renderFieldError('targetProductId')}
                        </div>
                    </div>
                    <p className="text-xs text-gray-500">
                        {t('promotion.promotionActions.buyQuantityHelperText')}
                    </p>

                    <div className="flex items-start gap-3 border-t pt-3">
                        <div className="w-[120px] shrink-0">
                            <Label className="text-xs">{t('promotion.promotionActions.rewardQuantity')}</Label>
                            <Input
                                type="number"
                                value={action.quantityToAdd || 1}
                                onChange={(e) => onUpdate(index, { quantityToAdd: parseInt(e.target.value) || 1 })}
                                className="mt-1 h-8"
                                min={1}
                            />
                        </div>
                        <div className="min-w-0 flex-1">
                            <Label className="text-xs font-medium">{t('promotion.promotionActions.rewardProduct')}</Label>
                            <div className="mt-1">
                                <SearchProductSingleVariantInput
                                    storeId={storeId}
                                    includeInactive
                                    productId={action.productIdToAdd}
                                    variantId={action.variantIdToAdd}
                                    onChange={({ productId, variantId }) =>
                                        onUpdate(index, { productIdToAdd: productId, variantIdToAdd: variantId })
                                    }
                                    onClear={() =>
                                        onUpdate(index, { productIdToAdd: undefined, variantIdToAdd: undefined })
                                    }
                                />
                            </div>
                            {renderFieldError('productIdToAdd')}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

